import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    FiEdit, FiTrash2, FiPrinter, FiTool, FiDollarSign, FiUser,
    FiTag, FiClipboard, FiHash, FiCalendar, FiDroplet, FiZap, FiTrendingUp,
    FiFilter, FiInbox, FiBriefcase, FiMessageSquare, FiCreditCard, FiClock,
    FiAlertTriangle, FiPaperclip, FiCheckCircle
} from 'react-icons/fi';
import { FaGasPump } from 'react-icons/fa'; // Importado para o ícone de consumo
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import generateVehicleReport from './generateVehicleReport'; 
import EditVeiculoPopup from './EditVeiculoPopup';
import ReportFieldsPopup from './ReportFieldsPopup';
import ManutencaoPopup from '../Manutencao/ManutencaoPopup';
import DespesasPopup from '../Despesas/DespesasPopup'; 
import VehicleFilesPopup from '../ArquivosVeiculo/VehicleFilesPopup';
import styles from './ViewVeiculo.module.css';
import PageHeader from '../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay'; 

const ViewVeiculo = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vehicle, setVehicle] = useState(null);
    const [consumoMedio, setConsumoMedio] = useState(null); // Estado para o consumo médio
    const [isLoading, setIsLoading] = useState(true); 
    const [pageLoading, setPageLoading] = useState(true); 
    const [error, setError] = useState(null);
    const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
    const [isReportPopupOpen, setIsReportPopupOpen] = useState(false);
    const [isManutencaoPopupOpen, setIsManutencaoPopupOpen] = useState(false);
    const [isDespesasPopupOpen, setIsDespesasPopupOpen] = useState(false);
    const [isFilesPopupOpen, setIsFilesPopupOpen] = useState(false);
    const [alertas, setAlertas] = useState([]);

    const handleCloseAlert = (id) => {
        setAlertas(currentAlertas =>
            currentAlertas.map(alerta =>
                alerta.id === id ? { ...alerta, visible: false } : alerta
            )
        );
    };

    const handleConfirmarManutencao = async (dados) => {
        const { valorAtual, unit } = dados;
        
        const result = await Swal.fire({
            title: 'Confirmar Manutenção?',
            text: `Confirmar que a manutenção foi feita aos ${valorAtual.toLocaleString('pt-BR')} ${unit}? Isso reiniciará o ciclo de alertas.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sim, confirmar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                
                await axios.patch(`${API_BASE_URL}/api/veiculos/${id}/ultima-manutencao`, {
                    ultima_manutencao: valorAtual
                }, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                Swal.fire('Registrado!', 'Ciclo de manutenção reiniciado com sucesso.', 'success');
                fetchVehicleById();

            } catch (err) {
                Swal.fire('Erro!', 'Não foi possível atualizar o registro.', 'error');
            }
        }
    };

    const verificarVencimentos = useCallback((vehicleData) => {
        if (!vehicleData) return;

        const novosAlertas = [];
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const diasAlerta = 30;

        const vencimentos = [
            { id: 'aet_nacional', tipo: 'AET (Nacional)', data: vehicleData.vencimentoAET, isDate: true },
            { id: 'aet_estadual', tipo: 'AET (Estadual)', data: vehicleData.vencimento_aet_estadual, isDate: true },
            { id: 'cronotacografo', tipo: 'Cronotacógrafo', data: vehicleData.vencimentoCronotacografo, isDate: true },
            { id: 'documentos', tipo: 'Licenças/Documentos', data: vehicleData.vencimentoDocumentos, isDate: true },
        ];

        vencimentos.forEach(({ id, tipo, data }) => {
            if (data) {
                const dataVencimento = new Date(data);
                const diffTime = dataVencimento.getTime() - hoje.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays < 0) {
                    novosAlertas.push({ 
                        id, 
                        tipo, 
                        status: 'danger', 
                        visible: true, 
                        msgCompleta: `A licença de ${tipo} está vencida há ${Math.abs(diffDays)} dia(s).`,
                        isMaintenance: false
                    });
                } else if (diffDays <= diasAlerta) {
                    novosAlertas.push({ 
                        id, 
                        tipo, 
                        status: 'warning', 
                        visible: true, 
                        msgCompleta: `A licença de ${tipo} vence em ${diffDays} dia(s).`,
                        isMaintenance: false
                    });
                }
            }
        });

        if (vehicleData.limite_manutencao && vehicleData.tipo_limite_manutencao) {
            const intervalo = parseFloat(vehicleData.limite_manutencao);
            const ultima = parseFloat(vehicleData.ultima_manutencao || 0);
            
            let atual = 0;
            let unit = '';
            let threshold = 0;

            if (vehicleData.tipo_limite_manutencao === 'quilometragem') {
                atual = parseFloat(vehicleData.quilometragem || 0);
                unit = 'Km';
                threshold = 1000;
            } else if (vehicleData.tipo_limite_manutencao === 'horimetro') {
                atual = parseFloat(vehicleData.horimetro || 0);
                unit = 'Horas';
                threshold = 50;
            }

            if (unit && intervalo > 0) {
                const proximaRevisao = ultima + intervalo;
                const diferenca = proximaRevisao - atual;

                const dadosParaBotao = { valorAtual: atual, unit };

                if (diferenca < 0) {
                    novosAlertas.push({
                        id: 'limite_manutencao',
                        tipo: `Manutenção Preventiva`,
                        status: 'danger',
                        visible: true,
                        msgCompleta: `CRÍTICO: Manutenção atrasada! Passou ${Math.abs(diferenca).toLocaleString('pt-BR')} ${unit} do ponto. (Prevista aos ${proximaRevisao.toLocaleString('pt-BR')} ${unit})`,
                        isMaintenance: true,
                        dadosManutencao: dadosParaBotao
                    });
                } else if (diferenca <= threshold) {
                    novosAlertas.push({
                        id: 'limite_manutencao',
                        tipo: `Manutenção Preventiva`,
                        status: 'warning',
                        visible: true,
                        msgCompleta: `ATENÇÃO: Faltam apenas ${diferenca.toLocaleString('pt-BR')} ${unit} para a manutenção. (Prevista aos ${proximaRevisao.toLocaleString('pt-BR')} ${unit})`,
                        isMaintenance: true,
                        dadosManutencao: dadosParaBotao
                    });
                }
            }
        }

        setAlertas(novosAlertas);
    }, []);

    // Função para buscar e calcular o consumo médio do veículo de forma consolidada
    const fetchVehicleConsumo = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/veiculos/${id}/despesas`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const despesasData = response.data;

            const abastecimentos = despesasData
                .filter(d => d.tipo_despesa === 'Combustível' && d.odometro !== null)
                .sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);

            let totalKmPercorrido = 0;
            let totalLitrosAbastecidos = 0;

            for (let i = 1; i < abastecimentos.length; i++) {
                const atual = abastecimentos[i];
                const anterior = abastecimentos[i - 1];

                const kmIntervalo = parseFloat(atual.odometro) - parseFloat(anterior.odometro);
                const litros = parseFloat(atual.quantidade_itens);

                if (kmIntervalo > 0 && litros > 0) {
                    totalKmPercorrido += kmIntervalo;
                    totalLitrosAbastecidos += litros;
                }
            }

            const media = totalLitrosAbastecidos > 0 ? totalKmPercorrido / totalLitrosAbastecidos : 0;
            setConsumoMedio(media);
        } catch (err) {
            console.error("Erro ao buscar consumo do veículo:", err);
        }
    }, [id]);

    const fetchVehicleById = useCallback(async () => {
        setIsLoading(true); 
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/veiculos/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            let vehicleData = response.data;
            
            // Puxa as despesas para conferir o maior odômetro lançado
            const despesasRes = await axios.get(`${API_BASE_URL}/api/veiculos/${id}/despesas`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Descobre o maior odômetro digitado nas despesas/abastecimentos
            const odometrosLancados = despesasRes.data
                .map(d => parseFloat(d.odometro))
                .filter(o => !isNaN(o) && o > 0);
                
            if (odometrosLancados.length > 0) {
                const maiorOdometro = Math.max(...odometrosLancados);
                // Se o odômetro das despesas for maior que o do cadastro, assume ele como o atual na tela
                if (maiorOdometro > (parseFloat(vehicleData.quilometragem) || 0)) {
                    vehicleData.quilometragem = maiorOdometro;
                }
            }

            setVehicle(vehicleData);
            verificarVencimentos(vehicleData);
            await fetchVehicleConsumo();
        } catch (err) {
            setError('Veículo não encontrado ou erro ao carregar os dados.');
        } finally {
            setIsLoading(false); 
        }
    }, [id, verificarVencimentos, fetchVehicleConsumo]);

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();
        setPageLoading(true); 

        const loadData = async () => {
            await fetchVehicleById(); 

            if (error && !error.includes('não encontrado')) {
                 Swal.fire('Erro!', error, 'error');
            } else if (!vehicle && !isLoading && !error) { 
                 Swal.fire('Erro!', 'Veículo não encontrado.', 'error').then(() => navigate('/sistema-frota'));
            }

            const endTime = Date.now();
            const duration = endTime - startTime;
            const remainingTime = MIN_LOADING_TIME - duration;

            if (remainingTime > 0) {
                setTimeout(() => setPageLoading(false), remainingTime);
            } else {
                setPageLoading(false); 
            }
        };

        loadData();
    }, [id, fetchVehicleById, navigate]); 

    const formatCurrency = useCallback((value) => {
        const number = parseFloat(value);
        if (isNaN(number)) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
    }, []);

    const formatKilometer = useCallback((km) => {
        if (isNaN(km) || km === null) return '0 km';
        return parseFloat(km).toLocaleString('pt-BR') + ' km';
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        return utcDate.toLocaleDateString('pt-BR');
    }, []);

    const handleDelete = useCallback(() => {
        if (!vehicle) return;
        Swal.fire({
            title: 'Tem certeza?',
            text: `Você realmente deseja excluir o veículo ${vehicle.marca} ${vehicle.modelo} (Placa: ${vehicle.placa || 'N/A'})?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE_URL}/api/veiculos/${id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    Swal.fire('Excluído!', 'O veículo foi removido com sucesso.', 'success').then(() => {
                        navigate('/frota/veiculos'); 
                    });
                } catch (err) {
                    Swal.fire('Erro!', 'Não foi possível excluir o veículo.', 'error');
                }
            }
        });
    }, [vehicle, id, navigate]);
    
    const handleEdit = () => {
        setIsEditPopupOpen(true);
    };

    const handleSaveAndClosePopup = () => {
        setIsEditPopupOpen(false);
        setPageLoading(true); 
        fetchVehicleById().finally(() => {
            setTimeout(() => setPageLoading(false), 200); 
        });
    };

    const handleGenerateCustomReport = useCallback((selectedFields) => {
        if (vehicle) {
            generateVehicleReport(vehicle, selectedFields, formatCurrency, formatKilometer, formatDate);
            Swal.fire({
                title: 'Relatório Gerado!',
                text: 'O download do seu PDF será iniciado em breve.',
                icon: 'success',
                timer: 3000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
        } else {
            Swal.fire('Erro!', 'Dados do veículo não disponíveis.', 'error');
        }
    }, [vehicle, formatCurrency, formatKilometer, formatDate]);

    const DetailItem = ({ icon, label, value }) => (
        <div className={styles.detailGroup}>
            <span className={styles.detailLabel}>
                {icon} {label}:
            </span>
            <span className={styles.detailValue}>{value}</span>
        </div>
    );

    return (
        <div className={styles.container}>
            {pageLoading && <LoadingOverlay text="Carregando detalhes..." />}

            {!pageLoading && (
                <>
                    {error && !vehicle ? (
                        <PageHeader title="Erro" subtitle={error} backTo="/frota/veiculos" />
                    ) : vehicle ? (
                        <>
                            <PageHeader 
                                title="Detalhes do Veículo"
                                subtitle="Informações completas, alertas de vencimento e ações rápidas."
                                backTo="/frota/veiculos"
                            />

                            <div className={styles.alertContainer}>
                                {alertas.filter(a => a.visible).map((alerta) => (
                                    <div key={alerta.id} className={`${styles.alertBox} ${alerta.status === 'danger' ? styles.alertDanger : styles.alertWarning}`}>
                                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                            <FiAlertTriangle className={styles.alertIcon} />
                                            <span className={styles.alertText}>
                                                {alerta.msgCompleta}
                                            </span>
                                        </div>
                                        
                                        {alerta.isMaintenance && (
                                            <button 
                                                onClick={() => handleConfirmarManutencao(alerta.dadosManutencao)}
                                                className={styles.actionBtn}
                                                style={{ 
                                                    padding: '0.4rem 0.8rem', 
                                                    fontSize: '0.85rem', 
                                                    marginLeft: '1rem',
                                                    backgroundColor: alerta.status === 'danger' ? '#b91c1c' : '#b45309',
                                                    border: 'none',
                                                    color: 'white',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title="Marcar como realizada no hodômetro atual"
                                            >
                                                <FiCheckCircle /> Já realizei
                                            </button>
                                        )}

                                        <button onClick={() => handleCloseAlert(alerta.id)} className={styles.alertCloseBtn}>
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.detailsCard}>
                                <div className={styles.cardHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                                    <h2>{vehicle.marca} {vehicle.modelo} ({vehicle.anoModelo || 'N/A'})</h2>
                                    
                                    {/* Bloco de Destaque Superior: Quilometragem / Horímetro Atual */}
                                    <div style={{
                                        backgroundColor: '#f1f5f9',
                                        padding: '0.6rem 1.2rem',
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.6rem',
                                        fontWeight: '600',
                                        color: '#1e293b'
                                    }}>
                                        <FiTrendingUp style={{ color: '#16a34a', fontSize: '1.2rem' }} />
                                        <span>
                                            {vehicle.tipoVeiculo === 'MAQUINA' ? 'Horímetro Atual:' : 'KM Atual:'}
                                        </span>
                                        <span style={{ color: '#16a34a', fontSize: '1.15rem', fontWeight: '700' }}>
                                            {vehicle.tipoVeiculo === 'MAQUINA' 
                                                ? `${vehicle.horimetro || 0} h` 
                                                : formatKilometer(vehicle.quilometragem)
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div className={styles.cardBody}>
                                    {vehicle.motorista_nome != null && (
                                        <>
                                            <DetailItem icon={<FiUser/>} label="Motorista" value={vehicle.motorista_nome} />
                                            {vehicle.motorista_cnh != null &&
                                                <DetailItem icon={<FiCreditCard/>} label="Nº Habilitação (CNH)" value={vehicle.motorista_cnh} />
                                            }
                                            <div className={styles.hr} />
                                        </>
                                    )}

                                    {vehicle.tipoVeiculo != null && <DetailItem icon={<FiTag/>} label="Tipo" value={vehicle.tipoVeiculo} />}
                                    {vehicle.placa != null && vehicle.placa.trim() !== '' && <DetailItem icon={<FiClipboard/>} label="Placa" value={vehicle.placa} />}
                                    {vehicle.renavam != null && vehicle.renavam.trim() !== '' && <DetailItem icon={<FiHash/>} label="RENAVAM" value={vehicle.renavam} />}
                                    {vehicle.chassi != null && vehicle.chassi.trim() !== '' && <DetailItem icon={<FiHash/>} label="Chassi" value={vehicle.chassi} />}
                                    {vehicle.anoFabricacao != null && vehicle.anoModelo != null && <DetailItem icon={<FiCalendar/>} label="Ano Fab/Mod" value={`${vehicle.anoFabricacao}/${vehicle.anoModelo}`} />}
                                    {vehicle.cor != null && vehicle.cor.trim() !== '' && <DetailItem icon={<FiDroplet/>} label="Cor" value={vehicle.cor} />}
                                    
                                    {vehicle.tipoVeiculo !== 'SEMIRREBOQUE' && vehicle.potenciaMotor != null && vehicle.potenciaMotor.trim() !== '' && (
                                        <DetailItem icon={<FiZap/>} label="Potência do Motor" value={vehicle.potenciaMotor} />
                                    )}

                                    {vehicle.tipoVeiculo === 'MAQUINA' ? (
                                        <DetailItem icon={<FiTrendingUp/>} label="Horímetro" value={`${vehicle.horimetro || 0} h`} />
                                    ) : (
                                        <DetailItem icon={<FiTrendingUp/>} label="KM" value={formatKilometer(vehicle.quilometragem)} />
                                    )}

                                    {vehicle.tipoVeiculo !== 'SEMIRREBOQUE' && vehicle.tipoVeiculo !== 'MAQUINA' && vehicle.tipoCombustivel != null && (
                                        <DetailItem icon={<FiFilter/>} label="Combustível" value={vehicle.tipoCombustivel} />
                                    )}
                                    
                                    {vehicle.tipoVeiculo !== 'SEMIRREBOQUE' && vehicle.tipoVeiculo !== 'MAQUINA' && vehicle.capacidadeTanque != null && (
                                        <DetailItem icon={<FiInbox/>} label="Tanque" value={`${vehicle.capacidadeTanque || 0} L`} />
                                    )}

                                    {/* EXIBIÇÃO DO CONSUMO MÉDIO (Apenas para veículos que usam combustível) */}
                                    {vehicle.tipoVeiculo !== 'SEMIRREBOQUE' && vehicle.tipoVeiculo !== 'MAQUINA' && (
                                        <DetailItem 
                                            icon={<FaGasPump style={{ color: '#16a34a' }} />} 
                                            label="Consumo Médio" 
                                            value={consumoMedio > 0 ? `${consumoMedio.toFixed(2)} KM/L` : 'N/A'} 
                                        />
                                    )}

                                    {vehicle.limite_manutencao && (
                                        <>
                                            <DetailItem 
                                                icon={<FiTool/>} 
                                                label="Intervalo Manutenção" 
                                                value={`A cada ${parseFloat(vehicle.limite_manutencao).toLocaleString('pt-BR')} ${vehicle.tipo_limite_manutencao === 'quilometragem' ? 'Km' : 'Horas'}`} 
                                            />
                                            {vehicle.ultima_manutencao > 0 && (
                                                <DetailItem 
                                                    icon={<FiCheckCircle/>} 
                                                    label="Última Realizada em" 
                                                    value={`${parseFloat(vehicle.ultima_manutencao).toLocaleString('pt-BR')} ${vehicle.tipo_limite_manutencao === 'quilometragem' ? 'Km' : 'Horas'}`} 
                                                />
                                            )}
                                        </>
                                    )}
                                    
                                    {vehicle.codigo_cc != null && vehicle.codigo_cc.trim() !== '' && (
                                        <DetailItem icon={<FiBriefcase/>} label="Código CC" value={vehicle.codigo_cc} />
                                    )}
                                    
                                    {vehicle.observacoes && vehicle.observacoes.trim() !== '' && (
                                        <div className={`${styles.detailGroup} ${styles.fullWidthDetail}`}>
                                            <span className={styles.detailLabel}><FiMessageSquare/> Observações:</span>
                                            <p className={styles.detailValueBlock}>{vehicle.observacoes}</p>
                                        </div>
                                    )}

                                    <div className={styles.hr} />
                                    
                                    <h3 className={styles.sectionTitle}><FiClock /> Controle de Vencimentos</h3>
                                    
                                    {(vehicle.vencimentoAET || vehicle.vencimento_aet_estadual || vehicle.vencimentoCronotacografo || vehicle.vencimentoDocumentos) ? (
                                        <>
                                            {vehicle.vencimentoAET && <DetailItem icon={<FiCalendar/>} label="Vencimento AET (Nacional)" value={formatDate(vehicle.vencimentoAET)} />}
                                            {vehicle.vencimento_aet_estadual && <DetailItem icon={<FiCalendar/>} label="Vencimento AET (Estadual)" value={formatDate(vehicle.vencimento_aet_estadual)} />}
                                            {vehicle.vencimentoCronotacografo && <DetailItem icon={<FiCalendar/>} label="Vencimento Cronotacógrafo" value={formatDate(vehicle.vencimentoCronotacografo)} />}
                                            {vehicle.vencimentoDocumentos && <DetailItem icon={<FiCalendar/>} label="Vencimento Licenças/Documentos" value={formatDate(vehicle.vencimentoDocumentos)} />}
                                        </>
                                    ) : (
                                        <DetailItem icon={<FiCalendar/>} label="Vencimentos" value="Nenhuma data informada." />
                                    )}
                                </div>

                                <div className={styles.cardFooter}>
                                    <button className={styles.actionBtn} onClick={handleEdit}><FiEdit /> Editar</button>
                                    <button className={styles.actionBtn} onClick={handleDelete}><FiTrash2 /> Excluir</button>
                                    <button className={styles.actionBtn} onClick={() => setIsReportPopupOpen(true)}><FiPrinter /> Relatório</button>
                                    <button className={styles.actionBtn} onClick={() => setIsManutencaoPopupOpen(true)}><FiTool /> Checklist</button>
                                    <button className={styles.actionBtn} onClick={() => setIsDespesasPopupOpen(true)}><FiDollarSign /> Despesas</button>
                                    <button className={styles.actionBtn} onClick={() => setIsFilesPopupOpen(true)}><FiPaperclip /> Arquivos</button>
                                </div>
                            </div>

                            {isEditPopupOpen && (
                                <EditVeiculoPopup
                                    vehicle={vehicle}
                                    onClose={() => setIsEditPopupOpen(false)}
                                    onSave={handleSaveAndClosePopup}
                                />
                            )}

                            {isReportPopupOpen && (
                                <ReportFieldsPopup
                                    vehicle={vehicle}
                                    onClose={() => setIsReportPopupOpen(false)}
                                    onGenerate={handleGenerateCustomReport}
                                />
                            )}

                            {isManutencaoPopupOpen && (
                                <ManutencaoPopup
                                    vehicle={vehicle}
                                    onClose={() => setIsManutencaoPopupOpen(false)}
                                />
                            )}

                            {isDespesasPopupOpen && (
                                <DespesasPopup
                                    vehicle={vehicle}
                                    onClose={() => {
                                        setIsDespesasPopupOpen(false);
                                        fetchVehicleConsumo(); 
                                        fetchVehicleById(); // Recarrega também para atualizar o KM se inserido nova despesa
                                    }}
                                />
                            )}

                            {isFilesPopupOpen && (
                                <VehicleFilesPopup
                                    vehicle={vehicle}
                                    onClose={() => setIsFilesPopupOpen(false)}
                                />
                            )}
                        </>
                    ) : null} 
                </>
            )}
        </div>
    );
};

export default ViewVeiculo;